// Where a floating window is and how big: { x, y, width, height } in pixels of the viewport ({ width, height }).

// How much of a window stays on screen while it's moved, so that it can always be taken back.
export const KEEP_VISIBLE = 48;
// The space between a window and the one it opens beside.
const GAP = 10;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

// A size no smaller than min and no larger than the viewport.
export const fitSize = (size, min, viewport) => ({
    width: clamp(Math.round(size.width), Math.min(min.width, viewport.width), viewport.width),
    height: clamp(Math.round(size.height), Math.min(min.height, viewport.height), viewport.height),
});

// The frame moved and shrunk into the viewport, as after the viewport gets smaller.
export const fitFrame = (frame, viewport) => {
    const width = Math.min(frame.width, viewport.width);
    const height = Math.min(frame.height, viewport.height);
    return { width, height, x: clamp(frame.x, 0, viewport.width - width), y: clamp(frame.y, 0, viewport.height - height) };
};

// Where a window of size opens: beside anchor, on its right, else on its left; else, when the two fit across the
// viewport, on its right with anchor moved to anchorX so that the two are in the middle together; else over
// anchor's right side, as far right as fits. In the middle of the viewport without an anchor.
export const placeBeside = (anchor, size, viewport) => {
    if (!anchor) {
        return { x: Math.round((viewport.width - size.width) / 2), y: Math.round((viewport.height - size.height) / 2) };
    }
    const y = clamp(anchor.y, 0, viewport.height - size.height);
    const right = anchor.x + anchor.width + GAP;
    if (right + size.width <= viewport.width) return { x: right, y };
    const left = anchor.x - GAP - size.width;
    if (left >= 0) return { x: left, y };
    const together = anchor.width + GAP + size.width;
    if (together <= viewport.width) {
        const anchorX = Math.round((viewport.width - together) / 2);
        return { x: anchorX + anchor.width + GAP, y, anchorX };
    }
    return { x: Math.max(0, viewport.width - size.width), y };
};

// The frame of a preset: 'height' keeps the width and fills the viewport's height, 'full' fills the viewport.
export const presetFrame = (preset, frame, viewport) => {
    if (preset === 'full') return { x: 0, y: 0, width: viewport.width, height: viewport.height };
    if (preset === 'height') return fitFrame({ ...frame, y: 0, height: viewport.height }, viewport);
    return fitFrame(frame, viewport);
};

// The frame moved by dx, dy from start, never above the viewport and never all the way out of it.
export const moveFrame = (start, dx, dy, viewport) => ({
    ...start,
    x: clamp(start.x + dx, KEEP_VISIBLE - start.width, viewport.width - KEEP_VISIBLE),
    y: clamp(start.y + dy, 0, viewport.height - KEEP_VISIBLE),
});

// The frame resized by dragging edge ('n', 'se', …) by dx, dy from start, within the viewport and no smaller
// than min.
export const resizeFrame = (start, edge, dx, dy, min, viewport) => {
    let { x, y, width, height } = start;
    if (edge.includes('e')) width = clamp(start.width + dx, min.width, viewport.width - start.x);
    if (edge.includes('s')) height = clamp(start.height + dy, min.height, viewport.height - start.y);
    if (edge.includes('w')) {
        x = clamp(start.x + dx, 0, start.x + start.width - min.width);
        width = start.x + start.width - x;
    }
    if (edge.includes('n')) {
        y = clamp(start.y + dy, 0, start.y + start.height - min.height);
        height = start.y + start.height - y;
    }
    return { x, y, width, height };
};
